import DeleteIcon from '@mui/icons-material/Delete'
import { IconButton, Tooltip } from '@mui/material'
import { MRT_RowData } from 'material-react-table'
import { useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'

import { useAppContext } from '@/hooks'
import { cn } from '@/utils/cn'

import { useElementBoundingRect } from '../hooks/useElementBoundingRect'
import { RowActionsCellProps } from '../types'
import { isInsertLineVisible } from '../utils/insertLineVisibility'
import { RowInsert } from './RowInsert'

const getRowElement = (anchor: HTMLElement) =>
	anchor.closest('tr') as HTMLElement | null

export const RowActionsCell = <TData extends MRT_RowData>({
	editMode = false,
	deleteRow,
	row,
	table,
	onInsertRow,
}: RowActionsCellProps<TData> & {
	onInsertRow?: (rowIndex: number) => void
}) => {
	const { t } = useAppContext()
	const cellRef = useRef<HTMLDivElement>(null)
	const getTarget = useCallback(getRowElement, [])
	const rowRect = useElementBoundingRect(cellRef, getTarget)

	const className = cn('flex items-center gap-1', {
		'cursor-not-allowed': !editMode,
	})

	const tableContainer = cellRef.current?.closest(
		'.MuiTableContainer-root',
	) as HTMLElement | null

	// MDD-2597: + inserts above the row below; not available under the last row (no row below)
	const rows = table.getRowModel().rows
	const isLastRow = rows.length > 0 && rows[rows.length - 1] === row
	const isFirstRow = row.index === 0

	const insertBelowVisible =
		!!rowRect && isInsertLineVisible(rowRect.bottom, tableContainer)
	const insertAboveVisible =
		!!rowRect && isInsertLineVisible(rowRect.top, tableContainer)

	const showInsertButton =
		editMode && onInsertRow && rowRect && !isLastRow && insertBelowVisible
	// Insert between header and first row: rowIndex -1 → insert at index 0
	const showInsertAboveFirstRow =
		editMode && onInsertRow && rowRect && isFirstRow && insertAboveVisible

	// Rect so the insert line/button stick to the top of the first row (line at rowRect.top)
	// RowInsert centers content in a 14px box at top: rect.bottom - 7; so line is at rect.bottom. Set rect.bottom = rowRect.top.
	const aboveFirstRowRect =
		showInsertAboveFirstRow && rowRect
			? {
					top: rowRect.top - 14,
					left: rowRect.left,
					width: rowRect.width,
					height: 14,
					bottom: rowRect.top,
				}
			: null

	return (
		<>
			<div ref={cellRef} className={className}>
				<Tooltip title={t('DELETE_ROW')}>
					<span>
						<IconButton
							onClick={() => deleteRow?.(row?.original.id || -1)}
							aria-label={t('REMOVE')}
							color="error"
							size="small"
							disabled={!editMode}
							data-testid={`table-row-${row.index}-button-delete`}
						>
							<DeleteIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			</div>
			{showInsertAboveFirstRow &&
				aboveFirstRowRect &&
				createPortal(
					<RowInsert
						rowIndex={-1}
						onInsertRow={onInsertRow}
						disabled={!editMode}
						rect={aboveFirstRowRect as DOMRect}
					/>,
					document.body,
				)}
			{showInsertButton &&
				createPortal(
					<RowInsert
						rowIndex={row.index}
						onInsertRow={onInsertRow}
						disabled={!editMode}
						rect={rowRect}
					/>,
					document.body,
				)}
		</>
	)
}
